import sodium from 'libsodium-wrappers-sumo';
import { generateMnemonic, mnemonicToEntropy } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
import type { KdfParams } from '../../../shared/protocol.ts';

/**
 * Schlüsselhierarchie (siehe docs/KONZEPT.md, Abschnitt 3):
 *
 *   Passphrase --Argon2id--> Master Key --KDF--> Auth-Key   (geht an den Server)
 *                                       \--KDF--> Wrap-Key  (bleibt hier)
 *                                                    |
 *                                              Vault Key --> Data Key pro Notiz
 *
 * Weder Master Key noch Vault Key verlassen jemals das Gerät.
 */

const KDF_CONTEXT_MASTER = 'athnMSTR';
const KDF_CONTEXT_RECOVERY = 'athnRCVR';
const SUBKEY_AUTH = 1;
const SUBKEY_WRAP = 2;
const ENVELOPE_VERSION = 1;

let ready: Promise<void> | null = null;

export function sodiumReady(): Promise<void> {
  ready ??= sodium.ready;
  return ready;
}

export const DEFAULT_KDF: KdfParams = { ops: 3, mem: 64 * 1024 * 1024, alg: 2 };

export function toB64(bytes: Uint8Array): string {
  return sodium.to_base64(bytes, sodium.base64_variants.ORIGINAL);
}

export function fromB64(text: string): Uint8Array {
  return sodium.from_base64(text, sodium.base64_variants.ORIGINAL);
}

/** Für Schlüssel, die im URL-Fragment stehen — dort haben +, / und = nichts verloren. */
export function toB64Url(bytes: Uint8Array): string {
  return sodium.to_base64(bytes, sodium.base64_variants.URLSAFE_NO_PADDING);
}

export function fromB64Url(text: string): Uint8Array {
  return sodium.from_base64(text, sodium.base64_variants.URLSAFE_NO_PADDING);
}

export function randomBytes(length: number): Uint8Array {
  return sodium.randombytes_buf(length);
}

/**
 * Verschlüsselt beliebige Bytes zu einem selbstbeschreibenden Envelope:
 * [Version 1 Byte][Nonce 24 Byte][Ciphertext inklusive Auth-Tag].
 */
export function seal(data: Uint8Array, key: Uint8Array): string {
  const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  const cipher = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(data, null, null, nonce, key);
  const envelope = new Uint8Array(1 + nonce.length + cipher.length);
  envelope[0] = ENVELOPE_VERSION;
  envelope.set(nonce, 1);
  envelope.set(cipher, 1 + nonce.length);
  return toB64(envelope);
}

export function open(envelopeB64: string, key: Uint8Array): Uint8Array {
  const envelope = fromB64(envelopeB64);
  if (envelope[0] !== ENVELOPE_VERSION) throw new Error('Unbekanntes Envelope-Format');
  const nonceLength = sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES;
  const nonce = envelope.slice(1, 1 + nonceLength);
  const cipher = envelope.slice(1 + nonceLength);
  return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(null, cipher, null, nonce, key);
}

/**
 * Wie `seal`, aber roh. Anhänge werden nicht base64-kodiert übertragen — das würde
 * jede Datei um ein Drittel aufblähen.
 */
export function sealBytes(data: Uint8Array, key: Uint8Array): Uint8Array {
  const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  const cipher = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(data, null, null, nonce, key);
  const envelope = new Uint8Array(1 + nonce.length + cipher.length);
  envelope[0] = ENVELOPE_VERSION;
  envelope.set(nonce, 1);
  envelope.set(cipher, 1 + nonce.length);
  return envelope;
}

export function openBytes(envelope: Uint8Array, key: Uint8Array): Uint8Array {
  if (envelope[0] !== ENVELOPE_VERSION) throw new Error('Unbekanntes Envelope-Format');
  const nonceLength = sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES;
  const nonce = envelope.slice(1, 1 + nonceLength);
  const cipher = envelope.slice(1 + nonceLength);
  return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(null, cipher, null, nonce, key);
}

export function sealText(text: string, key: Uint8Array): string {
  return seal(sodium.from_string(text), key);
}

export function openText(envelopeB64: string, key: Uint8Array): string {
  return sodium.to_string(open(envelopeB64, key));
}

type Derived = { authKey: string; wrapKey: Uint8Array };

function splitMaster(master: Uint8Array, context: string): Derived {
  const authKey = sodium.crypto_kdf_derive_from_key(32, SUBKEY_AUTH, context, master);
  const wrapKey = sodium.crypto_kdf_derive_from_key(32, SUBKEY_WRAP, context, master);
  return { authKey: toB64(authKey), wrapKey };
}

/**
 * Argon2id über die Passphrase. Schlägt die Ableitung mit dem gewünschten
 * Speicherbedarf fehl (ältere Mobilbrowser lassen 64 MiB im WASM-Heap nicht immer zu),
 * wird einmal halbiert. Die tatsächlich verwendeten Parameter landen beim Server und
 * werden beim nächsten Anmelden identisch wiederholt.
 */
export async function deriveMaster(
  passphrase: string,
  saltB64: string,
  params: KdfParams,
): Promise<{ derived: Derived; params: KdfParams }> {
  await sodiumReady();
  const salt = fromB64(saltB64);
  let attempt = params;
  for (;;) {
    try {
      const master = sodium.crypto_pwhash(32, passphrase, salt, attempt.ops, attempt.mem, attempt.alg);
      return { derived: splitMaster(master, KDF_CONTEXT_MASTER), params: attempt };
    } catch (error) {
      if (attempt.mem <= 16 * 1024 * 1024) throw error;
      attempt = { ...attempt, mem: Math.floor(attempt.mem / 2) };
      console.warn(`[crypto] Argon2id-Speicher auf ${attempt.mem / 1024 / 1024} MiB reduziert`);
    }
  }
}

export async function newRecoveryPhrase(): Promise<string> {
  await sodiumReady();
  return generateMnemonic(wordlist, 256);
}

export async function deriveRecovery(phrase: string): Promise<Derived> {
  await sodiumReady();
  // Wirft bei falscher Prüfsumme oder unbekannten Wörtern — Tippfehler fallen sofort auf.
  const entropy = mnemonicToEntropy(phrase.trim().toLowerCase().replace(/\s+/g, ' '), wordlist);
  return splitMaster(entropy, KDF_CONTEXT_RECOVERY);
}

export function newSalt(): string {
  return toB64(sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES));
}

export function newVaultKey(): Uint8Array {
  return sodium.randombytes_buf(32);
}

export function newDataKey(): Uint8Array {
  return sodium.randombytes_buf(32);
}
